import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HelpPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/help-requests`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit help request:', error);
      alert('Failed to send help request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const faqItems = [
    {
      question: "How do I create an account?",
      answer: "Currently, Megavideosee operates as a free platform without user registration. Simply verify your age and start browsing our premium content immediately."
    },
    {
      question: "Is the content really free?",
      answer: "Yes! All content on Megavideosee is completely free to watch. We are supported by advertisements to keep the service free for our users."
    },
    {
      question: "How do I report inappropriate content?",
      answer: "You can report content using our Report page or by contacting us directly. We take all reports seriously and review content within 24 hours."
    },
    {
      question: "Why can't I access the site?",
      answer: "Make sure you are 18+ years old and have completed the age verification. Also check that JavaScript is enabled in your browser."
    },
    {
      question: "How do I improve video quality?",
      answer: "All our videos are uploaded in HD quality. If you're experiencing quality issues, check your internet connection speed and try refreshing the page."
    },
    {
      question: "Can I download videos?",
      answer: "No, downloading videos is not permitted. All content is meant for online streaming only to protect copyright and our content creators."
    },
    {
      question: "How do I navigate the site?",
      answer: "Use the main navigation menu to browse categories, featured content, or latest uploads. The search function helps you find specific content quickly."
    },
    {
      question: "What browsers are supported?",
      answer: "Megavideosee works on all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, keep your browser updated."
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900">
        <header className="sticky top-0 z-50 glass-effect-dark border-b border-orange-500/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10"
                data-testid="back-home-btn"
              >
                ← Back to Home
              </Button>
              <div className="flex items-center space-x-3">
                <div className="text-2xl font-bold text-white">
                  <span className="text-gradient">X</span>videxa
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="min-h-screen flex items-center justify-center" data-testid="help-success">
          <Card className="glass-effect-dark max-w-md">
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-4">🎯</div>
              <h2 className="text-2xl font-bold text-orange-400 mb-4">Help Request Sent!</h2>
              <p className="text-gray-300 mb-6">
                Thank you for contacting our support team. We'll get back to you within 24 hours with assistance.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Back to Homepage
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="help-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-effect-dark border-b border-orange-500/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10"
              data-testid="back-home-btn"
            >
              ← Back to Home
            </Button>
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold text-white">
                <span className="text-gradient">X</span>videxa
              </div>
              <div className="text-orange-400 text-sm bg-orange-600/20 px-2 py-1 rounded border border-orange-500/50">
                Help Center
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="animate-slideInUp space-y-12">
          
          {/* Page Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold title-primary mb-4">
              Help Center
            </h1>
            <p className="text-gray-400 text-lg">
              Get assistance with using Megavideosee and find answers to common questions
            </p>
          </div>

          {/* Quick Help Categories */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="glass-effect-dark hover:border-orange-500/40 transition-colors cursor-pointer">
              <CardHeader>
                <div className="text-4xl mb-2">🎯</div>
                <CardTitle className="text-white">Getting Started</CardTitle>
                <CardDescription className="text-gray-400">
                  Learn how to navigate and use the platform
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="glass-effect-dark hover:border-orange-500/40 transition-colors cursor-pointer">
              <CardHeader>
                <div className="text-4xl mb-2">🛠️</div>
                <CardTitle className="text-white">Technical Issues</CardTitle>
                <CardDescription className="text-gray-400">
                  Troubleshoot playback and loading problems
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="glass-effect-dark hover:border-orange-500/40 transition-colors cursor-pointer">
              <CardHeader>
                <div className="text-4xl mb-2">📧</div>
                <CardTitle className="text-white">Contact Support</CardTitle>
                <CardDescription className="text-gray-400">
                  Get personalized help from our team
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="text-3xl font-bold title-primary mb-8 text-center">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass-effect-dark border border-orange-500/20 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-white hover:text-orange-400 text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-300 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Contact Form */}
          <Card className="glass-effect-dark">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-orange-400 text-center">
                Still Need Help?
              </CardTitle>
              <CardDescription className="text-center text-gray-400">
                Send us a detailed message and we'll get back to you soon
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your name"
                      data-testid="help-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="your@email.com"
                      data-testid="help-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="category" className="text-white mb-2 block">Help Category</Label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-input w-full"
                    data-testid="help-category-select"
                  >
                    <option value="general">General Help</option>
                    <option value="technical">Technical Issues</option>
                    <option value="content">Content Questions</option>
                    <option value="account">Account Issues</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-white mb-2 block">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Brief description of your issue"
                    data-testid="help-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-white mb-2 block">Detailed Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-input min-h-[120px] resize-none"
                    placeholder="Please provide as much detail as possible about your issue..."
                    data-testid="help-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary hover-lift text-lg py-3"
                  data-testid="help-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-pulse">Sending...</div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Send Help Request</span>
                      <span>🎯</span>
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpPage;